"use client"

import { useEffect, useMemo, useState } from "react"
import type { TrendingTag } from "@/lib/trending-tags"

export type TrendingState = {
  status: "idle" | "loading" | "ready" | "error"
  tags: TrendingTag[]
  /** Sources that contributed to the list. */
  sources: string[]
}

interface Answer {
  tags: TrendingTag[]
  sources: string[]
}

/** The list changes about hourly on the server, so a visitor keeps what they fetched for ten minutes. */
const CLIENT_TTL_MS = 10 * 60 * 1000
const cache = new Map<string, { at: number; answer: Answer }>()
const inFlight = new Map<string, Promise<Answer>>()

function load(key: string, limit: number): Promise<Answer> {
  const fresh = cache.get(key)
  if (fresh && Date.now() - fresh.at < CLIENT_TTL_MS) return Promise.resolve(fresh.answer)
  const running = inFlight.get(key)
  if (running) return running
  const request = fetch(`/api/trending/source-tags?sites=${encodeURIComponent(key)}&limit=${limit}`)
    .then((response) => (response.ok ? response.json() : Promise.reject(new Error(`status ${response.status}`))))
    .then((data: Answer) => {
      const answer = { tags: data.tags ?? [], sources: data.sources ?? [] }
      cache.set(key, { at: Date.now(), answer })
      return answer
    })
    .finally(() => inFlight.delete(key))
  inFlight.set(key, request)
  return request
}

/**
 * Trending characters, series and artists for the given sources, combined into one list (see app/api/trending/source-tags).
 * Nothing is requested until `enabled`, so a closed panel costs nothing. An empty list is a normal answer: sources that have
 * no trending list yet simply contribute nothing.
 */
export function useTrendingTags(sites: readonly string[], options: { enabled: boolean; limit?: number }): TrendingState {
  const { enabled, limit = 16 } = options
  const key = useMemo(() => [...new Set(sites)].sort().join(","), [sites])
  const [state, setState] = useState<TrendingState>({ status: "idle", tags: [], sources: [] })

  useEffect(() => {
    if (!enabled || !key) return
    let cancelled = false
    const cached = cache.get(key)
    if (cached && Date.now() - cached.at < CLIENT_TTL_MS) {
      setState({ status: "ready", tags: cached.answer.tags, sources: cached.answer.sources })
      return
    }
    setState((previous) => ({ ...previous, status: "loading" }))
    load(key, limit)
      .then((answer) => {
        if (!cancelled) setState({ status: "ready", tags: answer.tags, sources: answer.sources })
      })
      .catch(() => {
        if (!cancelled) setState({ status: "error", tags: [], sources: [] })
      })
    return () => {
      cancelled = true
    }
  }, [enabled, key, limit])

  return state
}
